/**
 * Vuex Quiz Module
 * Manages quiz state, answers, timing, and submission
 */

const quizModule = {
  namespaced: true,

  state: {
    currentQuiz: null,
    quizResult: null,
    loading: false,
    error: null,
    answers: [],
    startTime: null,
    questionTimestamps: []
  },

  mutations: {
    SET_QUIZ(state, quiz) {
      state.currentQuiz = quiz
      state.answers = []
      state.questionTimestamps = []
    },

    SET_QUIZ_RESULT(state, result) {
      state.quizResult = result
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    ADD_ANSWER(state, answer) {
      state.answers.push(answer)
    },

    SET_START_TIME(state, timestamp) {
      state.startTime = timestamp
    },

    ADD_QUESTION_TIMESTAMP(state, timestamp) {
      state.questionTimestamps.push(timestamp)
    },

    CLEAR_QUIZ(state) {
      state.currentQuiz = null
      state.quizResult = null
      state.answers = []
      state.startTime = null
      state.questionTimestamps = []
      state.error = null
    }
  },

  actions: {
    /**
     * Fetch quiz data from backend
     * @param {string} quizId - Quiz ID
     */
    async fetchQuiz({ commit }, quizId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/quizzes/${quizId}`
        )

        if (!response.ok) {
          throw new Error('Quiz not found')
        }

        const data = await response.json()
        commit('SET_QUIZ', data.quiz)
        commit('SET_START_TIME', Date.now())
        commit('SET_LOADING', false)

        return { success: true }
      } catch (error) {
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return { success: false, error: error.message }
      }
    },

    /**
     * Add answer with timing information
     * @param {string} questionId - Question ID
     * @param {number} selectedOptionId - Selected option ID
     */
    addAnswer({ commit, state }, { questionId, selectedOptionId }) {
      const currentTime = Date.now()
      const lastTimestamp = state.questionTimestamps[state.questionTimestamps.length - 1] || state.startTime
      const timeSpent = Math.floor((currentTime - lastTimestamp) / 1000)

      commit('ADD_ANSWER', {
        question_id: questionId,
        selected_option_id: selectedOptionId,
        time_spent: timeSpent
      })

      commit('ADD_QUESTION_TIMESTAMP', currentTime)
    },

    /**
     * Submit quiz answers to backend
     * @param {string} quizId - Quiz ID
     */
    async submitQuiz({ commit, state, rootState }, quizId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const token = rootState.auth.token
        if (!token) {
          throw new Error('Authentication required')
        }

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/quizzes/${quizId}/submit`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              answers: state.answers
            })
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Submission failed')
        }

        const data = await response.json()
        commit('SET_QUIZ_RESULT', data.quiz_result)
        commit('SET_LOADING', false)

        return {
          success: true,
          result: data.quiz_result,
          achievements: data.new_achievements,
          leaderboardUpdate: data.leaderboard_update
        }
      } catch (error) {
        commit('SET_ERROR', error.message)
        commit('SET_LOADING', false)
        return { success: false, error: error.message }
      }
    },

    /**
     * Reset quiz state
     */
    resetQuiz({ commit }) {
      commit('CLEAR_QUIZ')
    }
  },

  getters: {
    currentQuiz: state => state.currentQuiz,
    quizResult: state => state.quizResult,
    loading: state => state.loading,
    error: state => state.error,
    answersCount: state => state.answers.length,
    totalQuestions: state => state.currentQuiz?.question_count || 0,
    progress: (state, getters) => {
      if (getters.totalQuestions === 0) return 0
      return Math.floor((getters.answersCount / getters.totalQuestions) * 100)
    }
  }
}

export default quizModule
